import React, { useCallback, useEffect, useState } from 'react';

import { Button as RaButton, useNotify } from 'react-admin';
import { useTranslate } from '../common/language';
import { apiUrl } from '../common/constants';
import { headersAPIObjectAuth } from '../common/utils';

import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import MuiButton from '@material-ui/core/Button';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@material-ui/icons/Refresh';
import CancelIcon from '@material-ui/icons/Cancel';

const ACTIVE_MASSIVE_ACTION_KEY = 'active_massive_action_id';
const ACTIVE_MASSIVE_ACTION_UPDATED_KEY = 'active_massive_action_updated_at';
const ACTIVE_MASSIVE_ACTION_SNAPSHOT_KEY = 'active_massive_action_snapshot';
const ACTIVE_MASSIVE_ACTION_EVENT = 'activeMassiveActionChanged';

export const MASSIVE_ACTION_TRACKING_EVENT = ACTIVE_MASSIVE_ACTION_EVENT;
export const hasTrackedMassiveAction = () => Boolean(localStorage.getItem(ACTIVE_MASSIVE_ACTION_KEY));

export const clearTrackedMassiveAction = () => {
  localStorage.removeItem(ACTIVE_MASSIVE_ACTION_KEY);
  localStorage.removeItem(ACTIVE_MASSIVE_ACTION_UPDATED_KEY);
  localStorage.removeItem(ACTIVE_MASSIVE_ACTION_SNAPSHOT_KEY);
  window.dispatchEvent(new Event(ACTIVE_MASSIVE_ACTION_EVENT));
};

export const trackMassiveAction = (id, snapshot = {}) => {
  if (!id) return;
  localStorage.setItem(ACTIVE_MASSIVE_ACTION_KEY, id);
  localStorage.setItem(ACTIVE_MASSIVE_ACTION_UPDATED_KEY, new Date().getTime());
  localStorage.setItem(ACTIVE_MASSIVE_ACTION_SNAPSHOT_KEY, JSON.stringify({
    id,
    type: snapshot.type || 2,
    status: snapshot.status || 0,
    total_items: snapshot.total_items || 0,
    sent_items: snapshot.sent_items || 0,
    failed_items: snapshot.failed_items || 0,
    skipped_items: snapshot.skipped_items || 0,
    processing_items: snapshot.processing_items || 0
  }));
  window.dispatchEvent(new Event(ACTIVE_MASSIVE_ACTION_EVENT));
};

const fetchJson = (url, options = {}) => {
  return fetch(url, {
    ...options,
    headers: headersAPIObjectAuth()
  }).then(async response => {
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || 'Error connectant amb la API...');
    }
    return response.json();
  });
};

const actionTypes = {
  1: 'massive_actions.type_bookings_email',
  2: 'massive_actions.type_digital_cards'
};

const statuses = {
  0: 'massive_actions.status_queued',
  1: 'massive_actions.status_running',
  2: 'massive_actions.status_paused',
  3: 'massive_actions.status_completed',
  4: 'massive_actions.status_completed_with_errors',
  5: 'massive_actions.status_cancelled',
  6: 'massive_actions.status_failed'
};

const isCancellable = status => [0, 1, 2].includes(status);
const isFinished = status => [3, 4, 5, 6].includes(status);

const progressValue = action => {
  if (!action.total_items) return 100;
  return Math.round(((action.sent_items + action.failed_items + action.skipped_items) / action.total_items) * 100);
};

const ProgressBar = ({ action }) => {
  const total = action.total_items || 0;
  const sentItems = action.sent_items || 0;
  const skippedItems = action.skipped_items || 0;
  const failedItems = action.failed_items || 0;
  const cancelledItems = action.status === 5 ? Math.max(total - sentItems - skippedItems - failedItems, 0) : 0;
  const sent = total ? (sentItems / total) * 100 : 0;
  const skipped = total ? (skippedItems / total) * 100 : 0;
  const failed = total ? (failedItems / total) * 100 : 0;
  const cancelled = total ? (cancelledItems / total) * 100 : 0;

  return (
    <Box display="flex" width="100%" height="12px" bgcolor="#eceff1" borderRadius={6} overflow="hidden">
      <Box width={`${sent}%`} bgcolor="#2e7d32" />
      <Box width={`${skipped}%`} bgcolor="#90a4ae" />
      <Box width={`${failed}%`} bgcolor="#c62828" />
      <Box width={`${cancelled}%`} bgcolor="#b0bec5" />
    </Box>
  );
};

const formatDate = value => {
  if (!value) return '';
  return new Date(value).toLocaleString();
};

const formatDuration = seconds => {
  if (seconds === null || seconds === undefined || seconds < 0) return '';
  const value = Math.round(seconds);
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  const secs = value % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
};

const runningTime = action => {
  if (action.total_duration_seconds !== null && action.total_duration_seconds !== undefined) {
    return formatDuration(action.total_duration_seconds);
  }
  return formatDuration(action.elapsed_seconds);
};

const remainingTimeText = (translate, seconds) => {
  const value = formatDuration(seconds);
  return value ? translate('massive_actions.remaining_approx').replace('%{time}', value) : '';
};

const readTrackedSnapshot = () => {
  try {
    const raw = localStorage.getItem(ACTIVE_MASSIVE_ACTION_SNAPSHOT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

export const MassiveActions = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const isSuperAdmin = ['SIFAC'].includes(localStorage.getItem('permissions_groups'));
  const associationsId = isSuperAdmin ? 0 : localStorage.getItem('associations_id');

  const load = useCallback(() => {
    setLoading(true);
    fetchJson(`${apiUrl}/massive_actions?associations_id=${associationsId}&page=${page}&size=${rowsPerPage}&sort=id,DESC`)
      .then(data => {
        setActions(data.content || []);
        setTotal(data.totalElements || 0);
      })
      .catch(error => notify(error.message, 'error'))
      .finally(() => setLoading(false));
  }, [notify, associationsId, page, rowsPerPage]);

  useEffect(() => {
    load();
  }, [load]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const cancel = action => {
    setCancellingId(action.id);
    fetchJson(`${apiUrl}/massive_actions/${action.id}/cancel`, { method: 'POST' })
      .then(() => {
        notify(translate('massive_actions.cancelled_ok'), 'success');
        load();
      })
      .catch(error => notify(error.message, 'error'))
      .finally(() => setCancellingId(null));
  };

  return (
    <>
      <br/>
      <Typography variant="h5" className="grey8" align="center">
        {translate('massive_actions.title')}
      </Typography>
      <Box bgcolor="background.paper" padding="20px" className="settings_box">
        <Box display="flex" justifyContent="flex-end" alignItems="center" marginBottom="16px">
          <RaButton label="ra.action.refresh" onClick={load} disabled={loading}>
            <RefreshIcon />
          </RaButton>
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{translate('massive_actions.created_at')}</TableCell>
              {isSuperAdmin ? <TableCell>{translate('massive_actions.association')}</TableCell> : null}
              <TableCell>{translate('massive_actions.type')}</TableCell>
              <TableCell>{translate('massive_actions.status')}</TableCell>
              <TableCell>{translate('massive_actions.progress')}</TableCell>
              <TableCell>{translate('massive_actions.elements')}</TableCell>
              <TableCell>{translate('massive_actions.elapsed')}</TableCell>
              <TableCell>{translate('massive_actions.remaining')}</TableCell>
              <TableCell align="right">{translate('massive_actions.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {actions.map(action => (
              <TableRow key={action.id}>
                <TableCell>{formatDate(action.created_at)}</TableCell>
                {isSuperAdmin ? <TableCell>{action.association_name || translate('massive_actions.global_action')}</TableCell> : null}
                <TableCell>{translate(actionTypes[action.type] || 'massive_actions.type_unknown')}</TableCell>
                <TableCell>{translate(statuses[action.status] || 'massive_actions.status_unknown')}</TableCell>
                <TableCell style={{ minWidth: 220 }}>
                  <ProgressBar action={action} />
                  <Typography variant="caption" className="grey8">
                    {progressValue(action)}%
                  </Typography>
                </TableCell>
                <TableCell>
                  {action.sent_items}/{action.total_items}
                  {action.failed_items > 0 ? ` - ${translate('massive_actions.failed')}: ${action.failed_items}` : ''}
                  {action.skipped_items > 0 ? ` - ${translate('massive_actions.skipped')}: ${action.skipped_items}` : ''}
                </TableCell>
                <TableCell>{runningTime(action)}</TableCell>
                <TableCell>{isFinished(action.status) ? '' : formatDuration(action.estimated_remaining_seconds)}</TableCell>
                <TableCell align="right">
                  {isCancellable(action.status) ? (
                    <RaButton label={translate('massive_actions.cancel')} onClick={() => cancel(action)} disabled={cancellingId === action.id}>
                      <CancelIcon />
                    </RaButton>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
            {actions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isSuperAdmin ? 9 : 8}>{translate('massive_actions.empty')}</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          labelRowsPerPage={translate('ra.navigation.page_rows_per_page')}
          labelDisplayedRows={({ from, to, count }) => translate('ra.navigation.page_range_info')
            .replace('%{offsetBegin}', from)
            .replace('%{offsetEnd}', to)
            .replace('%{total}', count)}
          backIconButtonProps={{ 'aria-label': translate('ra.navigation.prev') }}
          nextIconButtonProps={{ 'aria-label': translate('ra.navigation.next') }}
        />
      </Box>
    </>
  );
};

export const MassiveActionMonitor = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [action, setAction] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(() => {
    const id = localStorage.getItem(ACTIVE_MASSIVE_ACTION_KEY);
    if (!id) {
      setAction(null);
      return;
    }
    const snapshot = readTrackedSnapshot();
    if (snapshot) {
      setAction(snapshot);
    }
    fetchJson(`${apiUrl}/massive_actions/${id}`)
      .then(data => {
        if (isFinished(data.status)) {
          clearTrackedMassiveAction();
          setAction(null);
        } else {
          localStorage.setItem(ACTIVE_MASSIVE_ACTION_SNAPSHOT_KEY, JSON.stringify(data));
          setAction(data);
        }
      })
      .catch(() => {
        if (!snapshot) {
          clearTrackedMassiveAction();
          setAction(null);
        }
      });
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 3000);
    window.addEventListener(ACTIVE_MASSIVE_ACTION_EVENT, load);
    window.addEventListener('storage', load);
    return () => {
      clearInterval(interval);
      window.removeEventListener(ACTIVE_MASSIVE_ACTION_EVENT, load);
      window.removeEventListener('storage', load);
    };
  }, [load]);

  const cancel = () => {
    if (!action) return;
    setCancelling(true);
    fetchJson(`${apiUrl}/massive_actions/${action.id}/cancel`, { method: 'POST' })
      .then(() => {
        notify(translate('massive_actions.cancelled_ok'), 'success');
        clearTrackedMassiveAction();
        setAction(null);
        load();
      })
      .catch(error => notify(error.message, 'error'))
      .finally(() => setCancelling(false));
  };

  if (!action) return null;

  return (
    <Box position="fixed" bottom="14px" right="18px" zIndex={1400} width="320px" bgcolor="#ffffff" border="1px solid #d8dde3" borderRadius="4px" boxShadow="0 4px 18px rgba(15, 23, 42, 0.18)" padding="12px">
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="8px">
        <Typography variant="body2" className="grey8">
          {translate(actionTypes[action.type] || 'massive_actions.type_unknown')}
        </Typography>
      </Box>
      <ProgressBar action={action} />
      <Box display="flex" justifyContent="space-between" alignItems="center" marginTop="8px">
        <Typography variant="caption" className="grey8">
          {action.sent_items}/{action.total_items} - {translate(statuses[action.status] || 'massive_actions.status_unknown')}
        </Typography>
        <Typography variant="caption" className="grey8">
          {remainingTimeText(translate, action.estimated_remaining_seconds)}
        </Typography>
      </Box>
      <Box display="flex" justifyContent="flex-end" marginTop="8px">
        <MuiButton size="small" color="secondary" onClick={cancel} disabled={cancelling} startIcon={<CancelIcon />}>
          {translate('massive_actions.cancel')}
        </MuiButton>
      </Box>
    </Box>
  );
};
